// Argus — WindowsTaskbar: Win11-style taskbar bottom.
//
// Două secțiuni:
//  1. Pinned (ordinea PANELS din AgentDashboard) — pentru selectedAgent. Panel-urile
//     deschise apar cu PC name; cele nedeschise apar ca pinned (doar icon).
//  2. Open cross-agent — ferestre deschise pe alți agenți decât selectedAgent.
//
// Click logic:
//  - Pinned (nedeschis) → openPanel.
//  - Open active (top z-order) → minimize.
//  - Open background (non-active) → bringToFront.
//  - Open minimized → restore + bringToFront.
//  - Right-click pe deschis → close.
//
// Border-bottom colored DOAR pentru fereastra activă (top z-order non-minimized).
// Clock 12h AM/PM + DD.MM.YYYY refresh 30s.

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, Typography } from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DesktopMacIcon from '@mui/icons-material/DesktopMac';
import VideocamIcon from '@mui/icons-material/Videocam';
import MicIcon from '@mui/icons-material/Mic';
import SettingsIcon from '@mui/icons-material/Settings';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import LinkIcon from '@mui/icons-material/Link';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BuildIcon from '@mui/icons-material/Build';
import TerminalIcon from '@mui/icons-material/Terminal';
import { useWindowStore, makeWindowKey, type WindowState } from '../../store/windowStore';
import { useSidebarStore } from '../../store/sidebarStore';
import { useAdminModalsStore } from '../../store/adminModalsStore';
import { restoreWindow, bringToFront, minimizeWindow, openPanel } from '../../lib/windowManager';
import { registerTaskbarButton } from '../../lib/taskbarButtonRefs';
import type { PanelType } from '../../lib/layoutPrefs';
import { fetchAgents, type Agent } from '../../lib/api';
import { ADMIN_UPDATES_WINDOW_KEY } from '../admin/UpdatesWindow';

const PANEL_ICON: Record<PanelType, { Icon: typeof FolderOpenIcon; color: string; label: string }> = {
  fileExplorer: { Icon: FolderOpenIcon, color: '#3B82F6', label: 'File Explorer' },
  desktop: { Icon: DesktopMacIcon, color: '#8B5CF6', label: 'Desktop' },
  webcam: { Icon: VideocamIcon, color: '#EC4899', label: 'Webcam' },
  mic: { Icon: MicIcon, color: '#F59E0B', label: 'Microphone' },
  systemInfo: { Icon: MonitorHeartIcon, color: '#10B981', label: 'System Info' },
  urlMonitoring: { Icon: LinkIcon, color: '#0EA5E9', label: 'URL Monitoring' },
  settings: { Icon: SettingsIcon, color: '#6B7280', label: 'Settings' },
  // Bloc task-manager-services 2026-05-07
  taskManager: { Icon: ListAltIcon, color: '#F97316', label: 'Task Manager' },
  services: { Icon: BuildIcon, color: '#14B8A6', label: 'Services' },
  // Bloc remote-powershell-shell 2026-05-08
  powerShell: { Icon: TerminalIcon, color: '#1F2937', label: 'PowerShell' },
};

// Aliniat cu PANELS array din AgentDashboard.tsx.
const PINNED_ORDER: ReadonlyArray<PanelType> = ['fileExplorer', 'desktop', 'webcam', 'systemInfo', 'urlMonitoring', 'taskManager', 'services', 'powerShell', 'settings'];

export function WindowsTaskbar(): JSX.Element | null {
  const openWindows = useWindowStore((s) => s.openWindows);
  const windowStack = useWindowStore((s) => s.windowStack);
  const selectedAgentId = useSidebarStore((s) => s.selectedAgentId);
  // Bloc msi-deployment 2026-05-06: ferestre globale admin (Updates Management).
  const adminUpdates = useAdminModalsStore((s) => s.updates);
  const adminMinimizeUpdates = useAdminModalsStore((s) => s.minimizeUpdates);
  const adminRestoreUpdates = useAdminModalsStore((s) => s.restoreUpdates);

  // Context menu state (right-click pe button → list agenți online).
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; panelType: PanelType } | null>(null);

  const { data: agents } = useQuery({
    queryKey: ['agents'],
    queryFn: fetchAgents,
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });
  const agentsById = useMemo(() => {
    const m = new Map<string, Agent>();
    if (agents) for (const a of agents) m.set(a.id, a);
    return m;
  }, [agents]);

  const onlineAgents = useMemo(
    () => (agents ?? []).filter((a) => a.isOnline),
    [agents]
  );

  // Active = top z-order non-minimized (cross-store: agent FloatingWindow + admin windows).
  // Bloc msi-deployment 2026-05-06: dacă admin updates window e deasupra, agent activeKey rămâne null.
  let topAgentKey: string | null = null;
  let topAgentZ = -Infinity;
  for (let i = windowStack.length - 1; i >= 0; i--) {
    const k = windowStack[i]!;
    const w = openWindows.get(k);
    if (w && !w.isMinimized) { topAgentKey = k; topAgentZ = w.zOrder; break; }
  }
  const adminUpdatesActive = adminUpdates.open && !adminUpdates.minimized && adminUpdates.zOrder > topAgentZ;
  const activeKey: string | null = adminUpdatesActive ? null : topAgentKey;

  // Pinned = panel-urile pentru selectedAgent în ordinea PINNED_ORDER.
  const pinnedItems = selectedAgentId
    ? PINNED_ORDER.map((panelType): { panelType: PanelType; agentId: string; key: string; win: WindowState | null } => {
        const key = makeWindowKey(selectedAgentId, panelType);
        const win = openWindows.get(key) ?? null;
        return { panelType, agentId: selectedAgentId, key, win };
      })
    : [];

  // Open cross-agent (alți agenți decât selectedAgent).
  const otherWindows: WindowState[] = [];
  for (const k of windowStack) {
    const w = openWindows.get(k);
    if (w && w.agentId !== selectedAgentId) otherWindows.push(w);
  }

  if (pinnedItems.length === 0 && otherWindows.length === 0 && !adminUpdates.open) {
    return null;
  }

  const handleClick = (key: string, agentId: string, panelType: PanelType, win: WindowState | null) => {
    if (win === null) {
      // Pinned (NU deschis) → openPanel.
      openPanel(panelType, agentId);
      return;
    }
    if (win.isMinimized) {
      restoreWindow(key);
      return;
    }
    if (key === activeKey) {
      minimizeWindow(key);
      return;
    }
    bringToFront(key);
  };

  const handleContextMenu = (e: React.MouseEvent, panelType: PanelType) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, panelType });
  };

  const handleSelectAgentFromMenu = (agentId: string) => {
    if (ctxMenu === null) return;
    const key = makeWindowKey(agentId, ctxMenu.panelType);
    const win = openWindows.get(key);
    if (win) {
      // Deja deschis — restore dacă minimized + bring to front.
      if (win.isMinimized) restoreWindow(key);
      else bringToFront(key);
    } else {
      // Open nou pentru agent selectat.
      openPanel(ctxMenu.panelType, agentId);
    }
    setCtxMenu(null);
  };

  return (
    <Box
      data-role="windows-taskbar"
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 40,
        backgroundColor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        zIndex: 5000,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexGrow: 1, overflow: 'hidden' }}>
        {pinnedItems.map(({ panelType, agentId, key, win }) => {
          const meta = PANEL_ICON[panelType];
          const Icon = meta.Icon;
          const agent = agentsById.get(agentId);
          const agentLabel = agent?.pcName || agent?.hostname || agentId.substring(0, 8);
          const isOpen = win !== null;
          const isActive = key === activeKey;
          const tooltipTitle = isOpen
            ? `${meta.label} — ${agentLabel}${win!.isMinimized ? ' (minimized)' : ''}`
            : `${meta.label} — ${agentLabel} (open)`;
          return (
            <Tooltip key={key} title={tooltipTitle} placement="top">
              <Box
                ref={(el: HTMLElement | null) => registerTaskbarButton(key, el)}
                role="button"
                onClick={() => handleClick(key, agentId, panelType, win)}
                onContextMenu={(e) => handleContextMenu(e, panelType)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isOpen ? 0.5 : 0,
                  px: isOpen ? 1 : 0.75,
                  py: 0.5,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  borderBottom: 2,
                  borderColor: isActive ? meta.color : 'transparent',
                  backgroundColor: isOpen ? 'action.hover' : 'transparent',
                  maxWidth: isOpen ? 200 : undefined,
                  '&:hover': { backgroundColor: 'action.selected' },
                }}
              >
                <Icon sx={{ fontSize: 20, color: meta.color, flexShrink: 0 }} />
                {isOpen ? (
                  <Typography
                    variant="caption"
                    sx={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                  >
                    {agentLabel}
                  </Typography>
                ) : null}
              </Box>
            </Tooltip>
          );
        })}

        {otherWindows.length > 0 && pinnedItems.length > 0 ? (
          <Box sx={{ width: 1, height: 24, backgroundColor: 'divider', mx: 0.5 }} />
        ) : null}

        {otherWindows.map((win) => {
          const meta = PANEL_ICON[win.panelType];
          const Icon = meta.Icon;
          const agent = agentsById.get(win.agentId);
          const agentLabel = agent?.pcName || agent?.hostname || win.agentId.substring(0, 8);
          const isActive = win.key === activeKey;
          const tooltipTitle = `${meta.label} — ${agentLabel}${win.isMinimized ? ' (minimized)' : ''}`;
          return (
            <Tooltip key={win.key} title={tooltipTitle} placement="top">
              <Box
                ref={(el: HTMLElement | null) => registerTaskbarButton(win.key, el)}
                role="button"
                onClick={() => handleClick(win.key, win.agentId, win.panelType, win)}
                onContextMenu={(e) => handleContextMenu(e, win.panelType)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  borderBottom: 2,
                  borderColor: isActive ? meta.color : 'transparent',
                  backgroundColor: 'action.hover',
                  maxWidth: 200,
                  '&:hover': { backgroundColor: 'action.selected' },
                }}
              >
                <Icon sx={{ fontSize: 20, color: meta.color, flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  sx={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                >
                  {agentLabel}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
        {adminUpdates.open ? (
          <>
            {(pinnedItems.length > 0 || otherWindows.length > 0) ? (
              <Box sx={{ width: 1, height: 24, backgroundColor: 'divider', mx: 0.5 }} />
            ) : null}
            <AdminUpdatesTaskbarButton
              isMinimized={adminUpdates.minimized}
              isActive={adminUpdatesActive}
              onClick={() => {
                if (adminUpdates.minimized) {
                  adminRestoreUpdates();
                } else if (adminUpdatesActive) {
                  adminMinimizeUpdates();
                } else {
                  useAdminModalsStore.getState().bringUpdatesToFront();
                }
              }}
            />
          </>
        ) : null}
      </Box>

      <Clock />

      <Menu
        open={ctxMenu !== null}
        onClose={() => setCtxMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctxMenu ? { top: ctxMenu.y, left: ctxMenu.x } : undefined}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        <MenuItem disabled dense sx={{ opacity: '0.7 !important' }}>
          <Typography variant="caption" color="text.secondary">
            {ctxMenu ? `Open ${PANEL_ICON[ctxMenu.panelType].label} for…` : ''}
          </Typography>
        </MenuItem>
        {onlineAgents.length === 0 ? (
          <MenuItem disabled dense>
            <Typography variant="caption" color="text.secondary">No agents online</Typography>
          </MenuItem>
        ) : (
          onlineAgents.map((agent) => {
            const label = agent.pcName || agent.hostname;
            const subLabel = agent.windowsUsername || agent.lastSeenUser || '';
            const isAlreadyOpen = ctxMenu
              ? openWindows.has(makeWindowKey(agent.id, ctxMenu.panelType))
              : false;
            return (
              <MenuItem
                key={agent.id}
                dense
                onClick={() => handleSelectAgentFromMenu(agent.id)}
              >
                <ListItemIcon>
                  <ComputerIcon fontSize="small" sx={{ color: 'success.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  secondary={subLabel}
                  slotProps={{
                    primary: { sx: { fontSize: 13 } },
                    secondary: { sx: { fontSize: 11 } },
                  }}
                />
                {isAlreadyOpen ? (
                  <Typography variant="caption" sx={{ ml: 1, color: 'primary.main', fontSize: 10 }}>
                    open
                  </Typography>
                ) : null}
              </MenuItem>
            );
          })
        )}
      </Menu>
    </Box>
  );
}

function AdminUpdatesTaskbarButton({
  isMinimized,
  isActive,
  onClick,
}: {
  isMinimized: boolean;
  isActive: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <Tooltip title={`Updates Management${isMinimized ? ' (minimized)' : ''}`} placement="top">
      <Box
        ref={(el: HTMLElement | null) => registerTaskbarButton(ADMIN_UPDATES_WINDOW_KEY, el)}
        role="button"
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 0.5,
          cursor: 'pointer',
          borderBottom: 2,
          borderColor: isActive ? '#0EA5E9' : 'transparent',
          backgroundColor: 'action.hover',
          flexShrink: 0,
          '&:hover': { backgroundColor: 'action.selected' },
        }}
      >
        <SystemUpdateAltIcon sx={{ fontSize: 20, color: '#0EA5E9', flexShrink: 0 }} />
        <Typography
          variant="caption"
          sx={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
        >
          Updates
        </Typography>
      </Box>
    </Tooltip>
  );
}

function Clock(): JSX.Element {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  const hour12 = ((now.getHours() + 11) % 12) + 1;
  const minute = now.getMinutes().toString().padStart(2, '0');
  const ampm = now.getHours() >= 12 ? 'PM' : 'AM';
  const date = `${now.getDate().toString().padStart(2, '0')}.${(now.getMonth() + 1).toString().padStart(2, '0')}.${now.getFullYear()}`;
  return (
    <Box data-role="taskbar-clock" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', px: 1, lineHeight: 1.1 }}>
      <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 500 }}>
        {hour12.toString().padStart(2, '0')}:{minute} {ampm}
      </Typography>
      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary' }}>
        {date}
      </Typography>
    </Box>
  );
}
